"use client";

import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import {
	COOKIE_NAME,
	type CookieConsentOptions,
	getConsentCookie,
	updateConsentCookie,
} from "./types";

interface CookieContextType {
	cookieConsent: CookieConsentOptions | null;
	showBanner: boolean;
	acceptAll: () => void;
	rejectAll: () => void;
	updatePreferences: (preferences: CookieConsentOptions) => void;
	openSettings: () => void;
	closeSettings: () => void;
	showSettings: boolean;
}

const defaultConsent: CookieConsentOptions = {
	necessary: true,
	analytics: false,
	marketing: false,
};

const CookieContext = createContext<CookieContextType>({
	cookieConsent: defaultConsent,
	showBanner: true,
	acceptAll: () => {},
	rejectAll: () => {},
	updatePreferences: () => {},
	openSettings: () => {},
	closeSettings: () => {},
	showSettings: false,
});

export function CookieConsentProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [cookies, setCookie] = useCookies([COOKIE_NAME]);
	const [showBanner, setShowBanner] = useState(true);
	const [showSettings, setShowSettings] = useState(false);

	useEffect(() => {
		const currentConsent = getConsentCookie(cookies);
		setShowBanner(!currentConsent);
	}, [cookies]);

	const acceptAll = () => {
		updateConsentCookie(cookies, setCookie, {
			analytics: true,
			marketing: true,
		});
		setShowBanner(false);
		setShowSettings(false);
	};

	const rejectAll = () => {
		updateConsentCookie(cookies, setCookie, {
			analytics: false,
			marketing: false,
		});
		setShowBanner(false);
		setShowSettings(false);
	};

	const updatePreferences = (preferences: CookieConsentOptions) => {
		updateConsentCookie(cookies, setCookie, preferences);
		setShowBanner(false);
		setShowSettings(false);
	};

	const openSettings = () => {
		setShowSettings(true);
	};

	const closeSettings = () => {
		setShowSettings(false);
	};
	return (
		<CookieContext.Provider
			value={{
				cookieConsent: getConsentCookie(cookies) || defaultConsent,
				showBanner,
				acceptAll,
				rejectAll,
				updatePreferences,
				openSettings,
				closeSettings,
				showSettings,
			}}
		>
			{children}
		</CookieContext.Provider>
	);
}

export const useCookieConsent = () => useContext(CookieContext);
